import React from 'react';
import { Check, X, Circle } from 'lucide-react';
import { passwordChecks, clinicPasswordChecks } from '../utils/passwordPolicy';

// Live per-rule strength checklist. Renders under the password field so
// the user sees each requirement flip from grey/red → green as they type.
// Hidden entirely until the user starts typing so we don't scare an empty
// field with red items.
//
// Props:
//   password  — current password string
//   header    — heading text, hide by passing null / '' to omit
//   variant   — 'default' (5 rules, ○ vs ✓) | 'clinic' (3 grouped rules,
//               ✗ red vs ✓ green, styled like the mobile mock)
//   checksFn  — override the rule set (advanced — for tests / custom flows)
function PasswordChecklist({
  password,
  header,
  variant = 'default',
  checksFn,
}) {
  const started = String(password || '').length > 0;
  if (!started) return null;

  const fn = checksFn
    || (variant === 'clinic' ? clinicPasswordChecks : passwordChecks);
  const checks = fn(password);

  const isClinic = variant === 'clinic';
  const finalHeader = header === undefined
    ? (isClinic ? null : 'Create a strong password')
    : header;

  return (
    <div
      style={{
        marginTop: 8,
        padding: '10px 12px',
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 8,
      }}
    >
      {finalHeader && (
        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-700)', marginBottom: 6 }}>
          {finalHeader}
        </div>
      )}
      <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 4 }}>
        {checks.map((c) => (
          <li
            key={c.key}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontSize: isClinic ? 13 : 12,
              color: c.passed
                ? 'var(--success, #16a34a)'
                : (isClinic ? 'var(--danger, #dc2626)' : 'var(--ink-500)'),
            }}
          >
            {c.passed
              ? <Check size={isClinic ? 16 : 14} strokeWidth={2.5} />
              : (isClinic
                  ? <X size={16} strokeWidth={2.5} />
                  : <Circle size={12} strokeWidth={2} />)
            }
            <span>{c.label}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default PasswordChecklist;
